import { useMemo } from 'react';

import { isImageEntry, isVideoEntry, type MediaEntry } from '../../lib/mediaEntries';
import { countByPresetPrefix, type PresetPrefix } from '../../lib/presetPrefixes';
import type { MultimediaSelection } from '../../lib/useMultimediaSelection';
import EmptyState from './EmptyState';
import ImageCard from './ImageCard';
import MediaTabSwitcher, { type MediaTab } from './MediaTabSwitcher';
import SelectionToolbar from './SelectionToolbar';

interface ImageSectionFilter {
  active: PresetPrefix | null;
  onChange: (prefix: PresetPrefix | null) => void;
}

interface ImageSectionProps {
  coverName: string | null;
  entries: MediaEntry[];
  filter: ImageSectionFilter;
  isDeleting: boolean;
  onBulkDownload: () => void;
  onBulkDuplicate: () => void;
  onDownload: (entry: MediaEntry) => void;
  onPreview: (entry: MediaEntry) => void;
  onRequestBulkDelete: () => void;
  onRequestBulkRename: () => void;
  onRequestDelete: (entry: MediaEntry) => void;
  onRequestRename: (entry: MediaEntry) => void;
  onTabChange: (tab: MediaTab) => void;
  selection: MultimediaSelection;
  tab: MediaTab;
}

const ImageSection = ({
  coverName,
  entries,
  filter,
  isDeleting,
  onBulkDownload,
  onBulkDuplicate,
  onDownload,
  onPreview,
  onRequestBulkDelete,
  onRequestBulkRename,
  onRequestDelete,
  onRequestRename,
  onTabChange,
  selection,
  tab,
}: ImageSectionProps) => {
  const imageEntries = useMemo(() => entries.filter(isImageEntry), [entries]);
  const videoEntries = useMemo(() => entries.filter(isVideoEntry), [entries]);
  const activeEntries = tab === 'videos' ? videoEntries : imageEntries;
  const prefixCounts = useMemo(() => countByPresetPrefix(activeEntries.map((e) => e.name)), [activeEntries]);
  const filteredEntries = useMemo(
    () => (filter.active === null ? activeEntries : activeEntries.filter((e) => e.name.startsWith(filter.active!))),
    [activeEntries, filter.active]
  );
  return (
    <>
      <MediaTabSwitcher
        imageCount={imageEntries.length}
        onChange={onTabChange}
        tab={tab}
        videoCount={videoEntries.length}
      />
      <SelectionToolbar
        active={filter.active}
        count={selection.count}
        counts={prefixCounts}
        disabled={isDeleting}
        onBulkDelete={onRequestBulkDelete}
        onBulkDownload={onBulkDownload}
        onBulkDuplicate={onBulkDuplicate}
        onBulkRename={onRequestBulkRename}
        onClear={selection.clear}
        onFilterChange={filter.onChange}
        onSelectAll={() => selection.selectAll(filteredEntries.map((e) => e.id))}
        total={filteredEntries.length}
      />
      {filteredEntries.length === 0 ? (
        <EmptyState text={tab === 'videos' ? 'No hay videos cargados.' : 'No hay imágenes cargadas.'} />
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4 p-5">
          {filteredEntries.map((entry) => (
            <ImageCard
              key={entry.id}
              disabled={isDeleting}
              entry={entry}
              isCover={entry.name === coverName}
              onDownload={() => onDownload(entry)}
              onPreview={() => onPreview(entry)}
              onRequestDelete={() => onRequestDelete(entry)}
              onRequestRename={() => onRequestRename(entry)}
              onToggleSelect={() => selection.toggle(entry.id)}
              selected={selection.isSelected(entry.id)}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default ImageSection;
